import React from 'react';
import {Stage as KonvaStage, Layer} from 'react-konva';
import ReactResizeDetector from 'react-resize-detector';
import ReactFireComponent from '../react-fire-component/react-fire-component';
import ImageLayer from './image-layer';
import PatternLayer from './pattern-layer';
import ParallaxLayer from './parallax-layer';
import AnimationLayer from './animation-layer';
import ScrollingLayer from './scrolling-layer';
import ScrollButton from './scroll-button';
import * as firebase from 'firebase';
import './stage.css';

/** @class Stage
 * A stage is the setting for a virtual expo,
 * composed of a background and foreground.
 */
export default class Stage extends ReactFireComponent {
  /** @constructor
    * Constructs a new instance of Stage.
    */
  constructor(props) {
    super(props);
    this.dataRef = firebase.database().ref(this.props.dataURL + '/layers');
    this.state = {
      width: 1024,
      height: 768,
      scale: 1,
      scaleToFit: false,
      scrollLeft: 0,
      scrollTop: 0,
      scrollWidth: 0,
      scrollHeight: 0,
      layers: []
    }
    this.scrollTo = this.scrollTo.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }
  /** @method componentWillMount
    * Binds the component's state to the firebase database.
    */
  componentWillMount() {
    this.bindAsObject(this.props.dataURL + '/scaleToFit', 'scaleToFit');
    this.bindAsObject(this.props.dataURL + '/width', 'width');
    this.bindAsObject(this.props.dataURL + '/height', 'height');
    this.bindAsArray(this.props.dataURL + '/layers', 'layers');
  }
  /** @method componentWillUnmount
    * Unbinds the component's state to the firebase database.
    */
  componentWillUnmount() {
    this.unbind('layers');
    this.unbind('height');
    this.unbind('width');
    this.unbind('scaleToFit');
  }
  /** @method componentDidMount
    * Adjusts the height of the stage area to stay
    * within the avialable client space.
    */
  componentDidMount() {
    this.wrapper.style.height = (window.innerHeight - this.wrapper.offsetTop)  + 'px';
    this.handleResize();
  }
  /** @method componentWillUpdate
    * Adjusts the height of the stage area to stay
    * within the avialable client space.
    */
  componentWillUpdate(nextProps, nextState) {
    this.wrapper.style.height = (window.innerHeight - this.wrapper.offsetTop)  + 'px';
  }
  /** @method componentDidUpdate
    * Triggers a handleResize call if the stage
    * should be scaled to fit the avialable space.
    */
  componentDidUpdate(prevProps, prevState) {
    if(prevState.scaleToFit !== this.state.scaleToFit) this.handleResize();
  }
  /** @method handleResize
    * Adjusts the stage to fit the available space
    */
  handleResize() {
    if(this.state.scaleToFit) {
      // determine viewport size and aspect ratio
      var width = this.wrapper.offsetWidth;
      var height = this.wrapper.offsetHeight;
      var aspectRatio = width/height;
      // determine scaling factor
      var scale = (aspectRatio > this.state.width/this.state.height) ?
        width / this.state.width :
        height / this.state.height;
      // Scroll to center
      var scrollTop = (this.state.height * scale - height) / 2;
      var scrollLeft = (this.state.width * scale - width ) / 2;
      this.viewport.scrollTop = scrollTop;
      this.viewport.scrollLeft = scrollLeft;
      // Update state
      this.setState({
        scale: scale,
        scrollLeft: scrollLeft,
        scrollTop: scrollTop
      });
    }
  }
  /** @method handleScroll
    * Handles scroll events on the stage.
    */
  handleScroll(event) {
    this.setState({
      scrollLeft: event.target.scrollLeft,
      scrollTop: event.target.scrollTop
    });
  }
  /** @method scrollTo
    * Scrolls to the specified location.
    * @prop {integer} scrollLeft - the number of pixels to scroll left
    * @prop {integer} scrollTop - the number of pixels to scroll down
    */
  scrollTo(scrollLeft, scrollTop) {
    const left = Math.max(0, Math.min(this.state.width - this.viewport.offsetWidth, scrollLeft));
    const top = Math.max(0, Math.min(this.state.height - this.viewport.offsetHeight, scrollTop));
    this.viewport.scrollLeft = left;
    this.viewport.scrollTop = top;
    this.setState({
      scrollLeft: left,
      scrollTop: top
    });
  }
  /** @function
   * Renders the Stage component.
   */
  render() {
    const layers = this.state.layers.map((layer, i) => {
      switch(layer.type) {
        case 'content':
          return this.props.children;
        case 'image':
          return (<ImageLayer key={layer.id} {...layer}/>);
        case 'pattern':
          return (<PatternLayer key={layer.id} width={this.state.width} height={this.state.height} {...layer}/>)
        case 'parallax':
          return (<ParallaxLayer key={layer.id} width={this.state.width} height={this.state.height} scrollLeft={this.state.scrollLeft} scrollTop={this.state.scrollTop} {...layer}/>)
        case 'scroll':
          return (<ScrollingLayer key={layer.id} width={this.state.width} height={this.state.height} {...layer}/>);
        case 'animation':
            return (<AnimationLayer key={layer.id} {...layer}/>);
        default:
          return null;
      }
    });
    var scrollButtons = [];
    scrollButtons.push(
      <ScrollButton direction="up" key="up"
        scroll={()=>this.scrollTo(this.state.scrollLeft, this.state.scrollTop - 5)}
      />
    );
    scrollButtons.push(
      <ScrollButton direction="down" key="down"
        scroll={()=>this.scrollTo(this.state.scrollLeft, this.state.scrollTop + 5)}
      />
    );
    scrollButtons.push(
      <ScrollButton direction="left" key="left"
        scroll={()=>this.scrollTo(this.state.scrollLeft - 5, this.state.scrollTop)}
      />
    );
    scrollButtons.push(
      <ScrollButton direction="right" key="right"
        scroll={()=>this.scrollTo(this.state.scrollLeft + 5, this.state.scrollTop)}
      />
    );
    return (
      <div ref={div => {this.wrapper = div}} className="stage">
        <ReactResizeDetector handleWidth handleHeight onResize={this.handleResize} />
        <div ref={div => {this.viewport = div}} className="viewport" onScroll={this.handleScroll}>
          <KonvaStage ref={div=>{this.stage = div}}
            width={this.state.width * this.state.scale}
            height={this.state.height * this.state.scale}
            scaleX={this.state.scale}
            scaleY={this.state.scale}
          >
            <Layer>
              {layers}
            </Layer>
          </KonvaStage>
        </div>
        {scrollButtons}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/stage/stage.js